import React from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  Divider,
  Avatar,
  Grid,
  Paper,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Sidebar from "../../components/sidebar/Sidebar";
import PageContainer from "../../components/layout/PageContainer";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useGetUserByIdQuery } from "../../store/apis/usersApi";
import { useGetCompaniesQuery } from "../../store/apis/companyApi";
import dayjs from "dayjs";
import EditIcon from "@mui/icons-material/Edit";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

const ViewUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  console.log(id);

  // Fetch user data
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
  } = useGetUserByIdQuery(id || "", { skip: !id });
  const user = userData?.data;

  // Get companies the user is attached to
  const { data: companiesData, isLoading: isLoadingCompanies } =
    useGetCompaniesQuery();
  const companies = companiesData?.data || [];
  console.log("companies", companies);
  console.log("userData", userData);

  // For now, show all companies - you'll need to implement your own logic
  // to determine if a user is associated with a company
  const userCompanies = companies;

  // Header with back button - used in both success and error states
  const pageHeader = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 4,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Button
          startIcon={<KeyboardBackspaceIcon />}
          onClick={() => navigate(-1)}
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          {t("common.back")}
        </Button>
        <Typography variant="h4" fontWeight="bold">
          {t("users.profile.title")}
        </Typography>
      </Box>
      {user && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/users/edit/${id}`)}
        >
          {t("users.edit")}
        </Button>
      )}
    </Box>
  );

  // Main content area
  const renderContent = () => {
    if (isLoadingUser || isLoadingCompanies) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    // Only show error if there's an actual API error
    if (userError) {
      return (
        <Paper
          sx={{
            p: 4,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            {t("users.errors.fetchError", { error: "API Error" })}
          </Typography>
          <Typography color={theme.palette.text.secondary}>
            {t("common.errorLoading")}
          </Typography>
        </Paper>
      );
    }

    if (!user) {
      return (
        <Paper
          sx={{
            p: 4,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="warning.main" gutterBottom>
            {t("users.notFoundTitle")}
          </Typography>
          <Typography color={theme.palette.text.secondary}>
            {t("users.notFoundMessage")}
          </Typography>
        </Paper>
      );
    }

    return (
      <>
        {/* User Profile Card */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Avatar
              alt={`${user.firstName} ${user.lastName}`}
              sx={{ width: 80, height: 80, mr: 3 }}
            >
              {user.firstName?.[0]}
              {user.lastName?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body1" color={theme.palette.text.secondary}>
                {user.role}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={t(`userStatus.${user.active ? "active" : "inactive"}`)}
                  sx={{
                    backgroundColor: user.active
                      ? theme.palette.success.main
                      : theme.palette.neutral["400"],
                    color: "white",
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* User Details */}
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {t("users.profile.yourData")}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.email")}
                </Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.phone")}
                </Typography>
                <Typography variant="body1">{user.phone || "-"}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.createdAt")}
                </Typography>
                <Typography variant="body1">
                  {user.createdAt && dayjs(user.createdAt).isValid()
                    ? dayjs(user.createdAt).format("MMM D, YYYY")
                    : "-"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.lastUpdate")}
                </Typography>
                <Typography variant="body1">
                  {user.updatedAt && dayjs(user.updatedAt).isValid()
                    ? dayjs(user.updatedAt).format("MMM D, YYYY")
                    : "-"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.firebaseUid")}
                </Typography>
                <Typography variant="body1">{user.firebaseUid}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Associated Companies */}
        <Paper
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
            {t("company.associatedCompanies")}
          </Typography>

          {userCompanies.length > 0 ? (
            <Grid container spacing={2}>
              {userCompanies.map((company) => (
                <Grid item xs={12} sm={6} md={4} key={company.id}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                    onClick={() => navigate(`/companies/${company.id}`)}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {company.nameCommercial || company.nameFiscal}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}
                    >
                      {company.email || "-"}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" color={theme.palette.text.secondary}>
                {t("company.noAssociatedCompanies")}
              </Typography>
            </Box>
          )}
        </Paper>
      </>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          {pageHeader}
          {renderContent()}
        </PageContainer>
      </Box>
    </Box>
  );
};

export default ViewUser;
